<template>
  <div class="effective-date-to-effective-to">
    <div class="date-fields-row">
      <!-- Effective from field (always shown) -->
      <div class="effective-date-field">
        <label class="field-label">Effective from*</label>
        <DatePicker
          class="effective-date-picker"
          :class="{ 'effective-date-picker--error': error }"
          :value="internalEffectiveFrom"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled-dates="disabledDates"
          :is-disable-datepicker="locked"
          :custom-calendar-icon="true"
          data-picker-place-holder="Select date"
          @onApplyDate="handleEffectiveFromChange"
        />
      </div>

      <!-- Read-only Effective to (plain text, no picker or add/remove link) -->
      <div v-if="effectiveToReadOnlyLabel" class="effective-date-field">
        <label class="field-label">Effective to</label>
        <span class="field-value">{{ effectiveToReadOnlyLabel }}</span>
      </div>

      <!-- Effective to field (conditionally shown) -->
      <div v-else-if="isEffectiveToVisible" class="effective-date-field">
        <label class="field-label">Effective to</label>
        <DatePicker
          class="effective-date-picker"
          :value="internalEffectiveTo"
          :min-date="minDate"
          :max-date="maxDate"
          :disabled-dates="effectiveToDisabledDates || disabledDates"
          :is-disable-datepicker="locked"
          :custom-calendar-icon="true"
          data-picker-place-holder="Select date"
          @onApplyDate="handleEffectiveToChange"
        />
      </div>

      <!-- Add/Remove effective to date button -->
      <button
        v-if="!effectiveToReadOnlyLabel && effectiveToEnabled && !isEffectiveToVisible"
        type="button"
        class="toggle-link-button"
        :disabled="locked"
        @click="addEffectiveTo"
      >
        <img
          :src="require('@components/CurrentPayPlanCard/icons/plus-secondary.svg')"
          alt=""
          class="button-icon"
        />
        <span class="button-text">Add effective to date</span>
      </button>
      <button
        v-else-if="!effectiveToReadOnlyLabel && isEffectiveToVisible"
        type="button"
        class="toggle-link-button"
        :disabled="locked"
        @click="removeEffectiveTo"
      >
        <span class="button-text">Remove effective to date</span>
      </button>
    </div>

    <div v-if="error || hint" class="field-hint" :class="{ 'field-hint--error': error }">
      {{ error || hint }}
    </div>

    <!-- Validation error message -->
    <div v-if="validationError" class="error-message">
      {{ validationError }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import DatePicker from '@components/DatePicker/DatePicker.vue'

const props = defineProps({
  effectiveFrom: {
    type: [String, Date, null],
    default: null
  },
  effectiveTo: {
    type: [String, Date, null],
    default: null
  },
  minDate: {
    type: [String, Date, null],
    default: null
  },
  maxDate: {
    type: [String, Date, null],
    default: null
  },
  disabledDates: {
    type: [Array, Function, null],
    default: null
  },
  /** Disabled dates for Effective to only; falls back to `disabledDates`. */
  effectiveToDisabledDates: {
    type: [Array, Function, null],
    default: null
  },
  /** When false the Effective to field and its add/remove link are hidden. */
  effectiveToEnabled: {
    type: Boolean,
    default: true
  },
  locked: {
    type: Boolean,
    default: false
  },
  /** When set, Effective to renders as this plain text; the picker and add/remove link are never shown. */
  effectiveToReadOnlyLabel: {
    type: String,
    default: null
  },
  /** Helper text under the row; replaced by `error` when that is set. */
  hint: {
    type: String,
    default: ''
  },
  /** Error text under the row (red); also puts a red border on the Effective from picker. */
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:effectiveFrom', 'update:effectiveTo'])

// Internal state
const internalEffectiveFrom = ref(props.effectiveFrom)
const internalEffectiveTo = ref(props.effectiveTo)
const showEffectiveToField = ref(props.effectiveTo != null)

// Computed properties
const isEffectiveToVisible = computed(
  () => props.effectiveToEnabled && showEffectiveToField.value
)

const validationError = computed(() => {
  if (!internalEffectiveFrom.value || !internalEffectiveTo.value) {
    return null
  }

  const fromDate = new Date(internalEffectiveFrom.value)
  const toDate = new Date(internalEffectiveTo.value)

  if (toDate < fromDate) {
    return 'Effective to date must be on or after Effective from date'
  }

  return null
})

// Watch for prop changes
watch(
  () => props.effectiveFrom,
  (newVal) => {
    internalEffectiveFrom.value = newVal
  }
)

watch(
  () => props.effectiveTo,
  (newVal) => {
    internalEffectiveTo.value = newVal
    showEffectiveToField.value = newVal != null
  }
)

// Event handlers
const handleEffectiveFromChange = (date) => {
  internalEffectiveFrom.value = date
  emit('update:effectiveFrom', date)
}

const handleEffectiveToChange = (date) => {
  internalEffectiveTo.value = date
  emit('update:effectiveTo', date)
}

const addEffectiveTo = () => {
  showEffectiveToField.value = true
}

const removeEffectiveTo = () => {
  showEffectiveToField.value = false
  internalEffectiveTo.value = null
  emit('update:effectiveTo', null)
}
</script>

<style scoped>
.effective-date-to-effective-to {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.date-fields-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}

.effective-date-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-label {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  white-space: nowrap;
  margin: 0;
}

.effective-date-picker :deep(.date-picker-display) {
  padding: 10px 14px;
  gap: 4px;
}

.effective-date-picker :deep(.date-picker-display span) {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  padding: 0 2px;
}

.effective-date-picker--error :deep(.date-picker-display) {
  border-color: #b42318;
}

.field-value {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  white-space: nowrap;
}

.field-hint {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.field-hint--error {
  color: #b42318;
}

.toggle-link-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #6941c6;
  white-space: nowrap;
}

.toggle-link-button:hover:not(:disabled) {
  opacity: 0.8;
}

.toggle-link-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.button-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.button-text {
  color: #6941c6;
}

.error-message {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #fecdca;
  border-radius: 8px;
}
</style>
